import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';

interface TimeSavedData {
  totalTimeSaved: number; // en secondes
  lastUpdated: string;
  summaryCount: number;
}

const STORAGE_KEY = 'abregecontenu_total_time_saved';

export const useTotalTimeSaved = () => {
  const { user, isAuthenticated } = useAuth();
  const [timeData, setTimeData] = useState<TimeSavedData>({
    totalTimeSaved: 0,
    lastUpdated: new Date().toISOString(),
    summaryCount: 0,
  });

  // Charger les données depuis l'utilisateur connecté ou localStorage
  useEffect(() => {
    if (isAuthenticated && user) {
      // Utiliser les données du serveur pour les utilisateurs connectés
      setTimeData({
        totalTimeSaved: user.totalMinutesSaved * 60, // Convertir minutes en secondes
        lastUpdated: new Date().toISOString(),
        summaryCount: 0, // TODO: Récupérer depuis le serveur si disponible
      });
    } else {
      // Utiliser localStorage pour les utilisateurs non connectés
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          setTimeData(parsed);
        } catch (error) {
          console.error('Erreur lors du parsing des données de temps:', error);
        }
      }
    }
  }, [isAuthenticated, user]);

  // Sauvegarder dans localStorage
  const saveToStorage = useCallback((data: TimeSavedData) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, []);

  // Ajouter du temps gagné
  const addTimeSaved = useCallback(
    (timeInSeconds: number) => {
      setTimeData((prev) => {
        const newData = {
          totalTimeSaved: prev.totalTimeSaved + timeInSeconds,
          lastUpdated: new Date().toISOString(),
          summaryCount: prev.summaryCount + 1,
        };
        saveToStorage(newData);
        return newData;
      });
    },
    [saveToStorage]
  );

  // Réinitialiser le compteur
  const resetTimeSaved = useCallback(() => {
    const newData = {
      totalTimeSaved: 0,
      lastUpdated: new Date().toISOString(),
      summaryCount: 0,
    };
    setTimeData(newData);
    saveToStorage(newData);
  }, [saveToStorage]);

  // Formater le temps en format lisible
  const formatTime = useCallback((seconds: number): string => {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);

    if (minutes < 60) {
      if (remainingSeconds === 0) {
        return `${minutes}min`;
      }
      return `${minutes}min ${remainingSeconds}s`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (remainingMinutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h ${remainingMinutes}min`;
  }, []);

  // Obtenir le temps formaté
  const formattedTime = formatTime(timeData.totalTimeSaved);

  // Obtenir des statistiques
  const getStats = useCallback(() => {
    const averageTimePerSummary =
      timeData.summaryCount > 0
        ? timeData.totalTimeSaved / timeData.summaryCount
        : 0;

    return {
      totalTimeSaved: timeData.totalTimeSaved,
      formattedTime,
      summaryCount: timeData.summaryCount,
      averageTimePerSummary,
      lastUpdated: timeData.lastUpdated,
    };
  }, [timeData, formattedTime]);

  return {
    totalTimeSaved: timeData.totalTimeSaved,
    formattedTime,
    summaryCount: timeData.summaryCount,
    addTimeSaved,
    resetTimeSaved,
    getStats,
  };
};
